import { useEffect, useState } from "react";
import { fetchIntakePicklists } from "../../api";
import type {
  DealPicklists,
  ExtractionResult,
  CreatePayload,
  EnrichPayload,
} from "../../types/intake";
import { IntakeReview } from "./IntakeReview";
import { ChatCapture } from "./ChatCapture";
import { PhotoCapture } from "./PhotoCapture";
import { VoiceCapture } from "./VoiceCapture";
import { FIELD_SPEC } from "./fieldSpec";

/** Render the deal's current values as a labeled list for the enrich Chat's
 *  context (so the conversation knows which deal it's editing). */
function formatDealContext(current: Record<string, unknown>): string {
  const lines: string[] = [];
  const dn = current.Deal_Name;
  if (typeof dn === "string" && dn.trim()) lines.push(`Deal: ${dn}`);
  for (const f of FIELD_SPEC) {
    if (f.target === "contact" || f.key === "Deal_Name") continue;
    const v = current[f.key];
    if (v == null || (typeof v === "string" && v.trim() === "")) continue;
    lines.push(`${f.label}: ${v}`);
  }
  if (current.CLTV != null) lines.push(`CLTV: ${current.CLTV}%`);
  return lines.join("\n");
}

interface Props {
  variant: "create" | "enrich";
  /** Enrich only — current Zoho values (conflict source) + the deal id. */
  current?: Record<string, unknown>;
  dealId?: string;
  onCreate?: (p: CreatePayload) => Promise<void>;
  onEnrich?: (p: EnrichPayload) => Promise<void>;
  /** Closes the whole intake (host owns the guardrail). */
  onCancel: () => void;
  /** Arms the host's dismiss guardrail on first input. */
  onDirty?: () => void;
}

type Mode = null | "manual" | "chat" | "voice" | "photo";

const EMPTY_EXTRACTION: ExtractionResult = { fields: {}, note: "", confidence: {}, unclear: [] };

/** Mode grid. Printable is intentionally absent — paper intake lives as a
 *  standalone PDF, hand-filled, then uploaded via Photo. */
const MODES: Array<{ key: Mode; icon: string; label: string; sub: string }> = [
  { key: "chat", icon: "💬", label: "Chat", sub: "talk it through" },
  { key: "voice", icon: "🎙", label: "Voice", sub: "record the call" },
  { key: "photo", icon: "📷", label: "Photo", sub: "snap or pick" },
  { key: "manual", icon: "⌨", label: "Manual", sub: "type it in" },
];

/**
 * IntakeCapture — the shared capture core used by BOTH Surface A (cockpit
 * Create) and Surface B (INTAKE NOTE Enrich). Owns the mode grid, the per-mode
 * capture panels, and rendering IntakeReview with the host's variant + submit
 * handler. AI modes produce an ExtractionResult that prefills the Review;
 * Manual opens an empty Review.
 */
export function IntakeCapture({
  variant,
  current,
  dealId,
  onCreate,
  onEnrich,
  onCancel,
  onDirty,
}: Props) {
  const [mode, setMode] = useState<Mode>(null);
  const [extraction, setExtraction] = useState<ExtractionResult | null>(null);
  const [picklists, setPicklists] = useState<DealPicklists | null>(null);
  const [picklistError, setPicklistError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchIntakePicklists()
      .then((p) => !cancelled && setPicklists(p))
      .catch((e) => !cancelled && setPicklistError(e instanceof Error ? e.message : "picklists_failed"));
    return () => {
      cancelled = true;
    };
  }, []);

  // Mode grid.
  if (mode === null) {
    return (
      <div className="intake-mode-grid">
        {MODES.map((m) => (
          <button
            key={m.key}
            className="intake-mode-tile"
            onClick={() => setMode(m.key)}
            title={`Capture via ${m.label}`}
          >
            <span className="intake-mode-icon">{m.icon}</span>
            <span className="intake-mode-label">{m.label}</span>
            <span className="intake-mode-sub">{m.sub}</span>
          </button>
        ))}
      </div>
    );
  }

  if (picklistError) {
    return <div className="write-confirm-error">⚠ Couldn't load Zoho options: {picklistError}</div>;
  }
  if (!picklists) {
    return (
      <div className="lme-loading">
        <span className="chat-thinking-dot" />
        <span className="chat-thinking-dot" />
        <span className="chat-thinking-dot" />
        <span className="chat-thinking-label">LOADING FORM</span>
      </div>
    );
  }

  // A capture mode that hasn't produced an extraction yet → its capture panel.
  if (mode === "chat" && !extraction) {
    return (
      <ChatCapture
        onExtracted={setExtraction}
        onCancel={onCancel}
        onDirty={onDirty}
        noteVariant={variant === "enrich" ? "call" : "intake"}
        dealContext={variant === "enrich" && current ? formatDealContext(current) : undefined}
      />
    );
  }
  if (mode === "photo" && !extraction) {
    return (
      <PhotoCapture
        onExtracted={setExtraction}
        onCancel={onCancel}
        onDirty={onDirty}
        noteVariant={variant === "enrich" ? "call" : "intake"}
      />
    );
  }
  if (mode === "voice" && !extraction) {
    return (
      <VoiceCapture
        onExtracted={setExtraction}
        onCancel={onCancel}
        onDirty={onDirty}
        noteVariant={variant === "enrich" ? "call" : "intake"}
        // Enrich (tab) has no host guardrail → VoiceCapture self-confirms an
        // active recording. Create (cockpit modal) already guards via touched.
        confirmOnDiscard={variant === "enrich"}
      />
    );
  }

  // Manual (empty) or any AI mode that has produced an extraction → Review.
  return (
    <IntakeReview
      variant={variant}
      extraction={mode === "manual" ? EMPTY_EXTRACTION : (extraction ?? EMPTY_EXTRACTION)}
      picklists={picklists}
      current={current}
      dealId={dealId}
      onCreate={onCreate}
      onEnrich={onEnrich}
      onCancel={onCancel}
      onDirty={onDirty}
    />
  );
}
